"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Card, CardBody, CardHeader, Row, Col, Badge, Spinner } from "reactstrap"
import { FaBuilding, FaEnvelope } from "react-icons/fa"
import { BadgeRequestService } from "../../../Service/BadgeRequestService"
import type { ExhibitorVerification } from "./badge-request"
import { toast } from "react-toastify"

interface Props {
  onExhibitorSelect: (exhibitor: ExhibitorVerification) => void
}

const ExhibitorsList: React.FC<Props> = ({ onExhibitorSelect }) => {
  const [exhibitors, setExhibitors] = useState<ExhibitorVerification[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchExhibitors()
  }, [])

  const fetchExhibitors = async () => {
    try {
      setLoading(true)
      const data = await BadgeRequestService.getVerifiedExhibitors()
      setExhibitors(data)
    } catch (error) {
      console.error("Failed to fetch exhibitors:", error)
      toast.error("Failed to load exhibitors")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <Spinner color="primary" />
      </div>
    )
  }

  if (exhibitors.length === 0) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <h5>No Verified Companies</h5>
          <p className="text-muted">You don't have access to any company yet.</p>
        </CardBody>
      </Card>
    )
  }

  return (
    <div>
      <Card>
        <CardHeader>
          <h5 className="mb-0">Select Company</h5>
          <small className="text-muted">Choose an company to request badges for</small>
        </CardHeader>
        <CardBody>
          <Row>
            {exhibitors.map((exhibitor) => (
              <Col md={6} lg={4} key={exhibitor.id} className="mb-3">
                <Card
                  className="h-100 cursor-pointer border-hover"
                  onClick={() => onExhibitorSelect(exhibitor)}
                  style={{ cursor: "pointer", transition: "all 0.2s" }}
                >
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center">
                        <FaBuilding className="text-primary me-2" />
                        <h6 className="mb-0">{exhibitor.exhibitor_name}</h6>
                      </div>
                      <Badge color={exhibitor.status === "verified" ? "success" : "warning"} pill>
                        {exhibitor.status}
                      </Badge>
                    </div>

                    <div className="text-muted small">
                      <div className="d-flex align-items-center mb-1">
                        <FaEnvelope className="me-2" size={12} />
                        {exhibitor.exhibitor_email}
                      </div>
                      <div className="text-xs">
                        Verified: {new Date(exhibitor.verified_at || exhibitor.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </CardBody>
      </Card>
    </div>
  )
}

export default ExhibitorsList
